// APR-1256 — pure summary of candidate-modal tab engagement for the
// candidate_decision_made analytics event. Kept side-effect free so the
// timing/visited logic can be table-tested without mounting the modal.
//
// Internal tab keys (`profile` = Work Style view, `cv` = attachments) are
// mapped to product-facing analytics vocabulary on the way out.

const TAB_TO_PRODUCT = {
  profile: 'profile', // Work Style view
  requirements: 'requirements',
  cv: 'attachments',
  history: 'history'
}

const productTab = tab => TAB_TO_PRODUCT[tab] || tab

/**
 * @param {Array<{tab: string, durationMs: number}>} segments
 *   Ordered list of tab dwell segments, one per time a tab was left, plus a
 *   final segment for the tab that was active when the decision was made.
 * @returns engagement summary in product-facing analytics vocabulary.
 */
export function summarizeTabEngagement(segments = []) {
  const times = { profile: 0, requirements: 0, cv: 0 }
  const visitedOrder = []

  for (const segment of segments) {
    const tab = segment && segment.tab
    if (!tab) continue

    if (tab in times) {
      times[tab] += Math.max(0, Number(segment.durationMs) || 0)
    }
    if (!visitedOrder.includes(tab)) {
      visitedOrder.push(tab)
    }
  }

  const activeTab = segments.length ? segments[segments.length - 1].tab : null
  const lastNonWorkStyle = [...visitedOrder]
    .reverse()
    .find(tab => tab !== 'profile')

  return {
    active_tab_at_decision: activeTab ? productTab(activeTab) : null,
    tabs_visited: visitedOrder.map(productTab),
    work_style_tab_viewed: visitedOrder.includes('profile'),
    requirements_tab_viewed: visitedOrder.includes('requirements'),
    attachments_tab_viewed: visitedOrder.includes('cv'),
    time_on_work_style_ms: Math.round(times.profile),
    time_on_requirements_ms: Math.round(times.requirements),
    time_on_attachments_ms: Math.round(times.cv),
    last_non_work_style_tab_visited: lastNonWorkStyle
      ? productTab(lastNonWorkStyle)
      : null
  }
}

const OVERVIEW_MODES = ['comparison', 'applicant', 'job']
const OVERVIEW_PATH_CAP = 20

export const EMPTY_OVERVIEW_ENGAGEMENT = {
  personality_mode_path: [],
  personality_modes_visited: [],
  personality_mode_at_leave: null,
  time_on_comparison_ms: 0,
  time_on_applicant_ms: 0,
  time_on_job_ms: 0
}

/**
 * @param {Array<{mode: string, durationMs: number}>} segments
 *   Ordered dwell segments for Comparison / Candidate / Role while Work Style
 *   was visible. Consecutive same-mode segments are pause/resume of one step.
 * @returns path-discovery summary. The path is the actual sequence, not a
 *   preferred order. Cap applies to the path only; times still sum every
 *   valid segment.
 */
export function summarizeOverviewEngagement(segments = []) {
  const times = { comparison: 0, applicant: 0, job: 0 }
  const path = []
  const visited = []
  let modeAtLeave = null

  for (const segment of segments) {
    const mode = segment && segment.mode
    if (!OVERVIEW_MODES.includes(mode)) continue

    times[mode] += Math.max(0, Number(segment.durationMs) || 0)
    modeAtLeave = mode

    if (path[path.length - 1] !== mode) {
      path.push(mode)
    }
    if (!visited.includes(mode)) {
      visited.push(mode)
    }
  }

  return {
    personality_mode_path: path.slice(0, OVERVIEW_PATH_CAP),
    personality_modes_visited: visited,
    personality_mode_at_leave: modeAtLeave,
    time_on_comparison_ms: Math.round(times.comparison),
    time_on_applicant_ms: Math.round(times.applicant),
    time_on_job_ms: Math.round(times.job)
  }
}
